// The venue's main screen: headline tiles, then leader boards ranked by cap,
// trading volume, mint + burn and 24h move, each with a share bar.

import type { Board, BoardToken } from "../api";
import { venueFlow } from "../all-venues";
import { anyMeasured, coverageNote, marketCapNote } from "../dashboard";
import { cleanFlow, flowMethodText, flowText } from "../flows";
import { partialNote, partialText } from "../partial";
import { compactUsd, count, percent, signedPercent, tokenName } from "../format";

function Tile({ label, value, note, title }: { label: string; value: string; note?: string; title?: string }) {
  return (
    <li className="tile" title={title}>
      <span className="tile-label">{label}</span>
      <span className="tile-value" data-absent={value === "—"}>
        {value}
      </span>
      {note ? <span className="tile-note">{note}</span> : null}
    </li>
  );
}

interface LeaderRow {
  token: BoardToken;
  value: number;
}

function Leaders({
  title,
  note,
  rows,
  format,
  total,
  signed = false,
}: {
  title: string;
  note: string;
  rows: LeaderRow[];
  format: (value: number) => string;
  /** Bars show share of this total; without one they scale to the largest row. */
  total?: number | null;
  signed?: boolean;
}) {
  const scale = total ?? Math.max(...rows.map((r) => Math.abs(r.value)), 0);
  return (
    <section className="panel" aria-label={title}>
      <header className="panel-head">
        <h2>{title}</h2>
        <span>{note}</span>
      </header>
      {rows.length === 0 ? (
        <p className="panel-empty">Not enough data yet.</p>
      ) : (
        <ol className="leaders">
          {rows.map(({ token, value }) => (
            <li key={token.id}>
              <span className="leader-symbol">{token.symbol}</span>
              <span className="leader-name">{tokenName(token.name)}</span>
              <span className={`leader-value${signed ? (value > 0 ? " up" : value < 0 ? " down" : "") : ""}`}>{format(value)}</span>
              <span className="leader-bar" aria-hidden>
                <span
                  className={signed && value < 0 ? "down-bar" : undefined}
                  style={{ width: `${scale > 0 ? Math.min(100, (Math.abs(value) / scale) * 100) : 0}%` }}
                />
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function top(tokens: BoardToken[], pick: (t: BoardToken) => number | null, n = 10, order: "desc" | "abs" = "desc"): LeaderRow[] {
  return tokens
    .flatMap((token) => {
      const value = pick(token);
      return value === null || !Number.isFinite(value) ? [] : [{ token, value }];
    })
    .sort((a, b) => (order === "abs" ? Math.abs(b.value) - Math.abs(a.value) : b.value - a.value))
    .slice(0, n);
}

export function Dashboard({ board }: { board: Board }) {
  const { totals, tokens } = board;
  const robinhood = board.venue.id === "robinhood";
  const byCap = top(tokens, (t) => t.market_cap);
  const topFive = byCap.slice(0, 5).reduce((sum, r) => sum + r.value, 0);
  const concentration = totals.market_cap ? topFive / totals.market_cap : null;
  const halted = tokens.filter((t) => t.halted).length;
  const movers = top(tokens, (t) => t.change_24h.price, 10, "abs");
  const traded = top(tokens, (t) => t.volume_usd);
  const flowWindow = robinhood ? "today" : "last 24h";
  const flow = venueFlow(board);
  const coverage = totals.coverage ?? {};
  // A tile's own note, followed by how much of the venue's value its total measures.
  const withCoverage = (note: string, metric: keyof typeof coverage, total: number | null) => {
    const extra = coverageNote(coverage[metric], total);
    return total === null ? (extra ?? note) : extra ? `${note} · ${extra}` : note;
  };

  return (
    <>
      <ul className="tiles">
        <Tile
          label="Tokenized market cap"
          value={compactUsd(totals.market_cap)}
          note={marketCapNote(board)}
        />
        {anyMeasured(coverage.volume_usd, totals.volume_usd) ? (
          <Tile
            label="Trading volume"
            value={compactUsd(totals.volume_usd)}
            note={withCoverage("venue-reported, today, USD", "volume_usd", totals.volume_usd)}
          />
        ) : null}
        <Tile
          label="Net flow · 24h"
          value={partialText(flow, flowText)}
          note={partialNote(flow) ?? (flowMethodText(totals.flow_method)?.toLowerCase() ?? "units issued minus redeemed")}
          title={
            totals.flow_method === "transfer_index" && totals.mint_burn_usd !== null ? `Minted + burned ${flowWindow}: ${compactUsd(totals.mint_burn_usd)}` : undefined
          }
        />
        <Tile label="Top 5 concentration" value={percent(concentration, 1)} note="share of tokenized cap" />
        {robinhood ? <Tile label="Trading halts" value={count(halted)} note={halted ? "tokens halted now" : "none halted"} /> : null}
      </ul>
      <div className="panels">
        <Leaders title="Largest by market cap" note="share of venue cap" rows={byCap} format={compactUsd} total={totals.market_cap} />
        {traded.length ? <Leaders title="Most traded" note="venue-reported volume, today" rows={traded} format={compactUsd} /> : null}
        <Leaders
          title="Net flow · 24h"
          note="largest in or out, units issued minus redeemed"
          rows={top(tokens, (t) => cleanFlow(t.net_flow_1d_usd), 10, "abs")}
          format={flowText}
          signed
        />
        <Leaders
          title="24h movers"
          note={movers.length ? "token price vs 24h ago" : "builds after 24h of history"}
          rows={movers}
          format={(v) => signedPercent(v)}
          signed
        />
      </div>
    </>
  );
}
